import { useCallback, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import AdministrationUsersPage from "@/features/administration/pages/AdministrationUsersPage";
import AdministrationTeamsPage from "@/features/administration/pages/AdministrationTeamsPage";
import {
  ensureFeatureSwitchesLoaded,
  useFeatureSwitchState,
} from "@/features/feature-switches/featureSwitchSession";

type PeopleTab = "members" | "teams";

function parseTab(raw: string | null): PeopleTab {
  return raw === "teams" ? "teams" : "members";
}

/**
 * People & Teams — URL-backed tabs over live org directory + admin teams APIs.
 * Members: ?tab=members (default). Teams: ?tab=teams.
 *
 * ADMIN-TEAMS-GATE-1: the Teams tab follows the `teams` feature switch. The
 * backend refuses every team route while it is off (default off), so an
 * always-visible tab was a control that could only fail. Off: no Teams tab, and
 * `?tab=teams` shows Members. While `GET /features` is in flight a `?tab=teams`
 * link shows nothing rather than flashing Members or calling the teams API.
 */
export default function AdministrationPeoplePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    ensureFeatureSwitchesLoaded();
  }, []);
  const teamsSwitch = useFeatureSwitchState("teams");
  const teamsOn = teamsSwitch === "on";
  const requested = useMemo(
    () => parseTab(searchParams.get("tab")),
    [searchParams],
  );
  const waitingForSwitch = requested === "teams" && teamsSwitch === "pending";
  const tab: PeopleTab = requested === "teams" && teamsOn ? "teams" : "members";

  const setTab = useCallback(
    (next: PeopleTab) => {
      const params = new URLSearchParams(searchParams);
      if (next === "members") {
        params.delete("tab");
      } else {
        params.set("tab", next);
      }
      setSearchParams(params, { replace: false });
    },
    [searchParams, setSearchParams],
  );

  return (
    <div className="space-y-6" data-testid="people-teams-page">
      <header>
        <h1 className="text-2xl font-bold text-gray-900">People &amp; Teams</h1>
        <p className="mt-1 text-sm text-gray-600">
          Manage organization members, access, and teams.
        </p>
      </header>

      {teamsOn ? (
        <div
          role="tablist"
          aria-label="People and teams"
          className="flex gap-1 border-b border-gray-200"
        >
          {(
            [
              { id: "members" as const, label: "Members" },
              { id: "teams" as const, label: "Teams" },
            ] as const
          ).map((item) => {
            const selected = tab === item.id;
            return (
              <button
                key={item.id}
                type="button"
                role="tab"
                id={`people-tab-${item.id}`}
                aria-selected={selected}
                aria-controls={`people-panel-${item.id}`}
                tabIndex={selected ? 0 : -1}
                data-testid={`people-tab-${item.id}`}
                className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
                  selected
                    ? "border-indigo-600 text-indigo-700"
                    : "border-transparent text-gray-600 hover:text-gray-900"
                }`}
                onClick={() => setTab(item.id)}
              >
                {item.label}
              </button>
            );
          })}
        </div>
      ) : null}

      <div
        role="tabpanel"
        id={`people-panel-${tab}`}
        aria-labelledby={`people-tab-${tab}`}
        data-testid={`people-panel-${tab}`}
      >
        {waitingForSwitch ? null : tab === "members" ? (
          <AdministrationUsersPage hidePageHeader />
        ) : (
          <AdministrationTeamsPage hidePageHeader />
        )}
      </div>
    </div>
  );
}
